// CollapsibleGroup component - Expandable group of catches
import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { convertWeight, getWeightUnit } from '../utils/unitConversion';
import type { GroupedCatches } from '../entities/CatchesPageTypes';
import type { CatchLog } from '../entities/FishingEntities';
import '../styles/CollapsibleGroup.css';

interface CollapsibleGroupProps {
  group: GroupedCatches;
  isExpanded: boolean;
  onToggle: () => void;
  renderCatchCard: (catchLog: CatchLog) => React.ReactNode;
}

export const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  group,
  isExpanded,
  onToggle,
  renderCatchCard
}) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();

  const { groupTitle, catches, stats } = group;

  return (
    <div className="collapsible-group">
      <button 
        className="collapsible-group-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="group-header-left">
          <span className="group-expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
          <h3 className="group-title">{groupTitle}</h3>
        </div>

        <div className="group-header-stats">
          <div className="group-stat">
            <span className="group-stat-value">{stats.totalCatches}</span>
            <span className="group-stat-label">
              {stats.totalCatches === 1 ? t.catch : t.catches}
            </span>
          </div>

          {stats.totalWeight > 0 && (
            <div className="group-stat">
              <span className="group-stat-value">
                {convertWeight(stats.totalWeight, unitSystem).toFixed(1)}
              </span>
              <span className="group-stat-label">{getWeightUnit(unitSystem)}</span>
            </div>
          )}
        </div>
      </button>

      <div 
        className={`collapsible-group-content ${isExpanded ? 'expanded' : 'collapsed'}`}
        aria-hidden={!isExpanded}
      >
        <div className="group-catches">
          {catches.map(catchLog => (
            <div key={catchLog.id} className="group-catch-item">
              {renderCatchCard(catchLog)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
